import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { api } from '../services/api.js';
import { Layout } from '../components/Layout.js';
import { useProfile } from '../hooks/useAuth.js';

interface InviteCode {
  id: string;
  code: string;
  used: boolean;
  usedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
}

interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: string;
  tier: string;
  subExpiresAt: string | null;
  createdAt: string;
}

// ── Small reusable atoms ──────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  function handleCopy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <button
      onClick={handleCopy}
      className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

function RoleBadge({ role }: { role: string }) {
  if (role === 'admin') {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
        Admin
      </span>
    );
  }
  return (
    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
      User
    </span>
  );
}

function TierBadge({ tier, expiresAt }: { tier: string; expiresAt: string | null }) {
  if (tier === 'pro') {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
        Pro
      </span>
    );
  }
  if (tier === 'trial') {
    const days = expiresAt
      ? Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86_400_000))
      : null;
    return (
      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
        Trial{days !== null ? ` · ${days}d` : ''}
      </span>
    );
  }
  return (
    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
      Free
    </span>
  );
}

// ── Edit modal ────────────────────────────────────────────────────────────────

function EditUserModal({
  user,
  onClose,
  onSaved,
}: {
  user: AdminUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  // trial can't be set directly; normalize to 'free' as starting point
  const origTier = user.tier === 'trial' ? 'free' : user.tier;
  const origExpiry = user.subExpiresAt ? user.subExpiresAt.slice(0, 10) : '';

  const [role, setRole] = useState(user.role);
  const [tier, setTier] = useState(origTier);
  const [expiry, setExpiry] = useState(origExpiry);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const tasks: Promise<unknown>[] = [];

      if (role !== user.role) {
        tasks.push(api.put(`/api/admin/users/${user.id}/role`, { role }));
      }
      if (tier !== origTier || expiry !== origExpiry) {
        const expiresAt = expiry ? new Date(expiry).toISOString() : null;
        tasks.push(api.put(`/api/admin/users/${user.id}/subscription`, { tier, expiresAt }));
      }

      await Promise.all(tasks);
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.body?.error ?? 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-sm shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Edit user</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 truncate">
          {user.name} &middot; {user.email}
        </p>

        <div className="space-y-4">
          {/* Role */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
              Role
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[--accent]"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {/* Tier */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
              Subscription tier
              {user.tier === 'trial' && (
                <span className="ml-1.5 font-normal text-amber-600 dark:text-amber-400">(currently trial)</span>
              )}
            </label>
            <select
              value={tier}
              onChange={(e) => setTier(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[--accent]"
            >
              <option value="free">Free</option>
              <option value="pro">Pro</option>
            </select>
          </div>

          {/* Expiry */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
              Expiry date{' '}
              <span className="font-normal text-gray-400">(leave blank for lifetime)</span>
            </label>
            <input
              type="date"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[--accent]"
            />
          </div>
        </div>

        {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="text-sm px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="text-sm font-semibold px-4 py-2 rounded-lg text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
            style={{ background: 'var(--accent)' }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function AdminPage() {
  const { data: profile, isLoading } = useProfile();
  const qc = useQueryClient();
  const [count, setCount] = useState(1);
  const [freshCodes, setFreshCodes] = useState<string[]>([]);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);

  const { data: adminUsers, isLoading: usersLoading } = useQuery<AdminUser[]>({
    queryKey: ['admin-users'],
    queryFn: () => api.get('/api/admin/users'),
    enabled: profile?.role === 'admin',
  });

  const { data: codes, isLoading: codesLoading } = useQuery<InviteCode[]>({
    queryKey: ['invite-codes'],
    queryFn: () => api.get('/api/admin/invite-codes'),
    enabled: profile?.role === 'admin',
  });

  const generate = useMutation({
    mutationFn: () =>
      api.post<{ codes: string[] }>('/api/admin/invite-codes', { count }),
    onSuccess: (data) => {
      setFreshCodes(data.codes);
      qc.invalidateQueries({ queryKey: ['invite-codes'] });
    },
  });

  if (isLoading) return null;
  if (profile?.role !== 'admin') return <Navigate to="/dashboard" replace />;

  const unused = codes?.filter((c) => !c.used).length ?? 0;
  const used = codes?.filter((c) => c.used).length ?? 0;

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Admin</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage users, subscriptions, and site access.
          </p>
        </div>

        {/* ── Users ─────────────────────────────────────────────────────── */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-medium text-gray-800 dark:text-gray-200">Users</h2>
              {adminUsers && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {adminUsers.length} total
                </p>
              )}
            </div>
          </div>

          {usersLoading ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">Loading…</p>
          ) : !adminUsers || adminUsers.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">No users yet.</p>
          ) : (
            <div className="overflow-x-auto -mx-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700">
                    {['Name', 'Email', 'Role', 'Tier', 'Expires', 'Joined', ''].map((h) => (
                      <th
                        key={h}
                        className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide px-4 pb-3 first:pl-6 last:pr-6"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                  {adminUsers.map((u) => (
                    <tr
                      key={u.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                    >
                      <td className="px-4 pl-6 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                        {u.name}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {u.email}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <RoleBadge role={u.role} />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <TierBadge tier={u.tier} expiresAt={u.subExpiresAt} />
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {u.subExpiresAt ? new Date(u.subExpiresAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {new Date(u.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 pr-6 py-3 text-right">
                        <button
                          onClick={() => setEditingUser(u)}
                          className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Generate invite codes ──────────────────────────────────────── */}
        <div className="card p-6">
          <h2 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-1">
            Generate invite codes
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Each code is single-use. Share one per person.
          </p>

          <div className="flex items-center gap-3">
            <select
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[--accent]"
            >
              {[1, 2, 3, 5, 10, 20, 50].map((n) => (
                <option key={n} value={n}>
                  {n} code{n > 1 ? 's' : ''}
                </option>
              ))}
            </select>
            <button
              onClick={() => {
                setFreshCodes([]);
                generate.mutate();
              }}
              disabled={generate.isPending}
              className="text-white text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
              style={{ background: 'var(--accent)' }}
            >
              {generate.isPending ? 'Generating…' : 'Generate'}
            </button>
          </div>

          {generate.isError && (
            <p className="mt-3 text-sm text-red-600 dark:text-red-400">
              {(generate.error as any)?.body?.error ?? 'Failed to generate codes.'}
            </p>
          )}

          {freshCodes.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                New codes
              </p>
              {freshCodes.map((code) => (
                <div
                  key={code}
                  className="flex items-center justify-between bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5"
                >
                  <span className="font-mono text-sm font-semibold tracking-widest text-gray-900 dark:text-white">
                    {code}
                  </span>
                  <CopyButton text={code} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Code history ──────────────────────────────────────────────── */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-medium text-gray-800 dark:text-gray-200">All codes</h2>
            <div className="flex gap-3 text-xs text-gray-500 dark:text-gray-400">
              <span>{unused} available</span>
              <span className="text-gray-300 dark:text-gray-600">·</span>
              <span>{used} used</span>
            </div>
          </div>

          {codesLoading ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">Loading…</p>
          ) : !codes || codes.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">No codes yet.</p>
          ) : (
            <div className="space-y-2">
              {codes.map((c) => (
                <div
                  key={c.id}
                  className={`flex items-center justify-between rounded-lg px-4 py-2.5 text-sm border ${
                    c.used
                      ? 'bg-gray-50 dark:bg-gray-900/50 border-gray-100 dark:border-gray-800'
                      : 'bg-surface border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <span
                    className={`font-mono tracking-widest font-medium ${
                      c.used
                        ? 'text-gray-400 dark:text-gray-600 line-through'
                        : 'text-gray-900 dark:text-white'
                    }`}
                  >
                    {c.code}
                  </span>
                  <div className="flex items-center gap-3">
                    {c.used ? (
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        Used {new Date(c.usedAt!).toLocaleDateString()}
                      </span>
                    ) : c.expiresAt ? (
                      <span className="text-xs text-amber-600 dark:text-amber-400">
                        Expires {new Date(c.expiresAt).toLocaleDateString()}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {new Date(c.createdAt).toLocaleDateString()}
                      </span>
                    )}
                    {!c.used && <CopyButton text={c.code} />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Edit modal (portal-like, rendered outside the scrollable area) ── */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['admin-users'] })}
        />
      )}
    </Layout>
  );
}
